import React from "react";
import { useLocation } from "react-router-dom";

import { updatePrivacyPreferences } from "@/api/account";
import { useAuth } from "@/contexts/AuthContext";
import { applyAnalyticsConsent, subscribeToAnalyticsConsent, trackScreenView } from "@/lib/analytics";
import {
  clearPrivacyConsent,
  getPrivacyConsent,
  PRIVACY_CONSENT_VERSION,
  setAnalyticsConsent,
} from "@/lib/privacyConsent";
import { getNativePlatform } from "@/lib/runtime";
import { cacheCompliance } from "@/lib/accountCompliance";
import queryClient from "@/lib/queryClient";
import { qk, useComplianceStatus, usePrivacyPreferences } from "@/queries/account";

export default function PrivacyConsentRuntime() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { data: serverPreferences } = usePrivacyPreferences();
  const { data: complianceStatus } = useComplianceStatus();
  const [localConsent, setLocalConsentState] = React.useState(() => getPrivacyConsent(user?.id));
  const syncedChoiceRef = React.useRef("");

  React.useEffect(() => {
    void applyAnalyticsConsent(false).catch((error) => {
      console.error("[Analytics] Kunde inte tillämpa samtycke", error);
    });

    return subscribeToAnalyticsConsent((consent) => {
      if (consent?.userId && String(consent.userId) !== String(user?.id || "")) return;
      setLocalConsentState(consent);
    });
  }, [user?.id]);

  React.useEffect(() => {
    setLocalConsentState(getPrivacyConsent(user?.id));
  }, [user?.id]);

  React.useEffect(() => {
    if (
      user?.id &&
      complianceStatus?.is_adult &&
      localConsent.source === "age_restriction"
    ) {
      clearPrivacyConsent(user.id);
    }
  }, [complianceStatus?.is_adult, localConsent.source, user?.id]);

  React.useEffect(() => {
    if (user?.id && complianceStatus) cacheCompliance(user.id, complianceStatus);
    const enabled = Boolean(
      complianceStatus?.complete
      && complianceStatus?.is_adult
      && localConsent.analyticsDecided
      && localConsent.analyticsEnabled
    );
    void applyAnalyticsConsent(enabled).catch((error) => {
      console.error("[Analytics] Kunde inte uppdatera samtycke", error);
    });
  }, [complianceStatus, localConsent.analyticsDecided, localConsent.analyticsEnabled, user?.id]);

  React.useEffect(() => {
    if (
      localConsent.analyticsDecided ||
      localConsent.source === "review" ||
      !serverPreferences?.analytics_decided ||
      !complianceStatus?.is_adult
    ) return;
    setAnalyticsConsent(serverPreferences.analytics_enabled === true, "server_sync", user?.id);
  }, [complianceStatus?.is_adult, localConsent.analyticsDecided, serverPreferences, user?.id]);

  React.useEffect(() => {
    if (
      !user?.id ||
      !complianceStatus?.is_adult ||
      !localConsent.analyticsDecided ||
      !["web_banner", "native_prompt"].includes(localConsent.source)
    ) return;
    const syncKey = `${user.id}:${localConsent.updatedAt}:${localConsent.analyticsEnabled}`;
    if (syncedChoiceRef.current === syncKey) return;
    syncedChoiceRef.current = syncKey;
    void updatePrivacyPreferences({
      analytics_enabled: localConsent.analyticsEnabled,
      consent_version: PRIVACY_CONSENT_VERSION,
      source:
        localConsent.source ||
        (getNativePlatform() === "android" ? "native_prompt" : "web_banner"),
    }).then((preferences) => {
      queryClient.setQueryData(qk.privacyPreferences, preferences);
    }).catch((error) => {
      syncedChoiceRef.current = "";
      console.error("[Privacy] Kunde inte synka analysval", error);
    });
  }, [
    localConsent.analyticsDecided,
    localConsent.analyticsEnabled,
    localConsent.source,
    localConsent.updatedAt,
    user?.id,
    complianceStatus?.is_adult,
  ]);

  React.useEffect(() => {
    if (!complianceStatus?.complete || !complianceStatus?.is_adult) return;
    void trackScreenView(pathname).catch((error) => {
      console.error("[Analytics] Kunde inte registrera sidvisning", error);
    });
  }, [complianceStatus?.complete, complianceStatus?.is_adult, localConsent.analyticsEnabled, localConsent.analyticsDecided, pathname]);

  return null;
}
